import UserVerifyModal from '@/Components/UserVerifyModal';
import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { UserRoleEnums, roleOptions } from '@/Interfaces/I_userRole';
import TagLabel from '@/Modules/tagLabel';
import UserRoleLabel from '@/Modules/userRoleLabel';
import { deleteUserVerify, getUserVerify } from '@/Redux/slices/UserVerifySlice';
import { Button, Empty, Input, Select, Table } from 'antd';
import { FilterValue } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react'

const { Search } = Input;

const UserVerify = () => {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchOption, setSearchOption] = useState('hybrisId');
  const [keyword, setKeyword] = useState<string>("");
  const [isAddNewVerifyUserVisible, setIsAddNewVerifiedUserVisible] = useState(false);
    const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue>>({
      userRole: [],
    });
  const userListLoading = useAppSelector((state) => state.userVerify.loading);
  const userList = useAppSelector((state) => state.userVerify.value?.userRoles?.data);
  const pagination = useAppSelector((state) => state.userVerify.value.userRoles.pagination);

  const searchOptions = [
    {
      value: 'hybrisId',
      label: 'Pkey',
    },
    {
      value: 'name',
      label: 'User Name',
    },
    {
      value: 'uuid',
      label: 'User UUID',
    },
  ];

  const userRoleFilters = roleOptions.map(role => ({
      text: role.label,
      value: role.value,
    }));

  const columns =[
    {
      title: 'No.',
      dataIndex: 'no',
      key: 'no',
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      render: (text: string) => <p className="h-6 line-clamp-1 break-all m-0">{text}</p>,
      width: '30%',
    },
    {
      title: 'User Role',
      dataIndex: 'userRole',
      key: 'userRole',
      render: (userRole: UserRoleEnums) => {
        return (
          <UserRoleLabel userRoleStr={userRole} />
        )
      },
      filters: roleOptions.filter((option) => option.value != "ALL").map((option) => (
        {
          text: option.label,
          value: option.value
        })
      ),
      filterMultiple: false,
    },
    {
      title: 'Membership',
      dataIndex: 'membershipLevel',
      key: 'membershipLevel',
      width: 107,
      className: 'pr-0',
      render: (text: string | null) =>
        (text !== null) && (
          <TagLabel
            labelType="level"
            levelType={text.toLowerCase() as 'normal' | 'vip' | 'goldvip' | 'diamondvip'}
          />          
        ),
    },
    {
      title: 'User PKey',
      dataIndex: 'hybrisId',
      key: 'hybrisId',
    },
    {
      title: 'User Uuid',
      dataIndex: 'uuid',
      key: 'uuid'
    },
    {
      title: '',
      dataIndex: 'removeButton',
      key: 'removeButton',
      render: (removeParam: { userUuid: string; removeOnClick: (userUuid: string) => void }) => {
        const { userUuid, removeOnClick } = removeParam;
        return (
          userUuid && (
            <Button danger onClick={() => removeOnClick(userUuid)}>
              REMOVE
            </Button>
          )
        );
      },
      width: '10%',
    }
  ];

  const removeOnClick = (userUuid: string) => {
    let roleFilter;
    if (filteredInfo?.userRole !== null && filteredInfo?.userRole[0] !== 'all') {
      roleFilter = filteredInfo?.userRole[0];
    }
    const deleteParam = {
      deleteUserUuid: userUuid,
      hybrisId: searchOption === 'hybrisId' ? keyword.trim() : null,
      name: searchOption === 'name' ? keyword.trim() : null,
      uuid: searchOption === 'uuid' ? keyword.trim() : null,
      kocUserRole: roleFilter,
      pageNo: currentPage - 1,
      pageSize: pageSize,
    }
    dispatch(deleteUserVerify(deleteParam));
  }

  const searchUser = () => {
    let roleFilter;
    if (!!filteredInfo?.userRole && filteredInfo?.userRole[0] !== 'ALL') {
      roleFilter = filteredInfo?.userRole[0];
    }
    dispatch(
      getUserVerify({
        hybrisId: searchOption === 'hybrisId' ? keyword.trim() : null,
        name: searchOption === 'name' ? keyword.trim() : null,
        uuid: searchOption === 'uuid' ? keyword.trim() : null,
        kocUserRole: roleFilter,
        pageNo: currentPage - 1,
        pageSize: pageSize,
      }),
    );
  }

  useEffect(() => {
    searchUser();
  }, [
    dispatch,
    filteredInfo,
    currentPage,
    pageSize,
    searchOption,
    keyword,
    ,
  ])

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex pb-4 justify-between">
        <Button
        type="primary"
          onClick={() => {
            setIsAddNewVerifiedUserVisible(true)
          }}
        >
          Add Verify User
        </Button>

        <div className="flex ml-auto items-center">
          <div className="pr-4">
            <Select
              style={{ width: 200 }}
              defaultValue={searchOption}
              options={searchOptions}
              onChange={(value) => {
                setSearchOption(value);
              }}
              bordered={false}
            />
          </div>
          <Search
            placeholder="input text"
            allowClear
            onSearch={(value) => {
              setKeyword(value.trim());
              setCurrentPage(1);
            }}
            style={{ width: 264 }}
            className="pr-4"
          />
        </div>
      </div>
      <div>
        <Table
          className="mt-4"
          columns={columns}
          dataSource={userList?.map((data,index) => ({
            key: data.uuid,
            no: pagination.page * pagination.pageSize + index + 1,
            userName: data.name,
            hybrisId: data.hybrisId,
            uuid: data.uuid,
            userRole: data.kocUserRole,
            membershipLevel:
              data.membershipLevel || null,
            removeButton: {
              userUuid: data.uuid,
              removeOnClick,
          },
          }))}
          onChange={(pagination, filters, sorter) => {
            const filterInfo = filters as Record<string, FilterValue>;
            setFilteredInfo(filterInfo);
          }}
          pagination={{
            total: pagination ? pagination.total : 0,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: pageSize,
            defaultCurrent: 1,
            showSizeChanger: true,
            current: currentPage,
            onChange: (page: number, selectPageSize: number) => {
              setCurrentPage(page);
            },
            onShowSizeChange: (current, size) => {
              setPageSize(size);
            },
          }}
        />
      </div>
      <UserVerifyModal 
        isAddNewVerifyUserVisible={isAddNewVerifyUserVisible}
        setIsAddNewVerifyUserVisible={setIsAddNewVerifiedUserVisible}
      />
    </div>
  )
};

export default UserVerify;